import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { onCommand } from '../lib/commandBus';
import {
  clearEmails,
  clearPulledDomains,
  clearSeenEmails,
  hideEmail,
  loadContactAppendMode,
  loadEmails,
  loadPulledDomains,
  loadSeenEmails,
  markDomainPulled,
  mergeEmails,
  saveContactAppendMode,
  saveHiddenEmails,
  unhideEmail,
} from '../lib/storage';
import ExportEmailsModal from './ExportEmailsModal';
import ContactFilters from './ContactFilters';
import { DataTable, GroupTr, Td, TdAction, Tr } from './DataTable';
import { Chip, classificationTone, verificationTone } from './Chip';

const API_URL = process.env.REACT_APP_API_URL || '/api/check-domain';

function normalizeDomain(domain) {
  return String(domain || '')
    .trim()
    .toLowerCase()
    .replace(/^www\./, '');
}

function formatShortDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function ContactsView({
  domains,
  companies = [],
  emails,
  onEmailsChange,
  hiddenEmails = [],
  onHiddenEmailsChange,
  onRunningChange,
  emailFilters,
  onEmailFiltersChange,
  filteredEmails,
  onGoEmails,
}) {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [stats, setStats] = useState({ added: 0, skipped: 0, dupes: 0 });
  const [error, setError] = useState(null);
  const [blacklistOpen, setBlacklistOpen] = useState(false);
  const [blacklistDraft, setBlacklistDraft] = useState('');
  const [exportOpen, setExportOpen] = useState(false);
  const [appendMode, setAppendMode] = useState(() => loadContactAppendMode());
  const abortRef = useRef(null);
  const blacklistRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (blacklistRef.current && !blacklistRef.current.contains(e.target)) {
        setBlacklistOpen(false);
      }
    };
    if (blacklistOpen) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [blacklistOpen]);

  const companyByDomain = useMemo(() => {
    const map = new Map();
    companies.forEach((c) => {
      if (c?.domain) map.set(c.domain, c);
    });
    return map;
  }, [companies]);

  const [pulledRevision, setPulledRevision] = useState(0);

  const loadedDomainSet = useMemo(() => {
    const set = loadPulledDomains();
    emails.forEach((row) => {
      const key = normalizeDomain(row?.domain);
      if (key) set.add(key);
    });
    return set;
  }, [emails, pulledRevision]);

  const remainingDomains = useMemo(
    () => domains.filter((d) => !loadedDomainSet.has(normalizeDomain(d))),
    [domains, loadedDomainSet]
  );

  const savedCount = loadedDomainSet.size;
  const savedOnList = domains.filter((d) =>
    loadedDomainSet.has(normalizeDomain(d))
  ).length;

  const grouped = useMemo(() => {
    const byDomain = new Map();
    for (const row of filteredEmails) {
      const key = row.domain || 'unknown';
      if (!byDomain.has(key)) byDomain.set(key, []);
      byDomain.get(key).push(row);
    }
    const domainOrder = [];
    const seen = new Set();
    for (const c of companies) {
      if (c.domain && byDomain.has(c.domain) && !seen.has(c.domain)) {
        domainOrder.push(c.domain);
        seen.add(c.domain);
      }
    }
    for (const key of byDomain.keys()) {
      if (!seen.has(key)) domainOrder.push(key);
    }
    return domainOrder.map((domain) => ({
      domain,
      company: companyByDomain.get(domain),
      contacts: byDomain.get(domain) || [],
    }));
  }, [filteredEmails, companies, companyByDomain]);

  const setRunningSafe = (value) => {
    setRunning(value);
    onRunningChange?.(value);
  };

  const startGrab = async (options = {}) => {
    if (!domains.length) {
      setError('No domains yet. Scrape Product Hunt first.');
      return;
    }

    if (!appendMode && emails.length) {
      if (!options.skipConfirm) {
        if (!window.confirm('Replace all existing contacts? Append is off.')) return;
      }
      clearEmails();
      clearSeenEmails();
      clearPulledDomains();
      onEmailsChange([]);
      setPulledRevision((n) => n + 1);
    }

    const alreadyLoaded = appendMode ? new Set(loadedDomainSet) : new Set();

    const skippedTargets = [];
    const targets = [];
    domains.forEach((domain) => {
      const key = normalizeDomain(domain);
      if (appendMode && key && alreadyLoaded.has(key)) skippedTargets.push(domain);
      else targets.push(domain);
    });

    const skipped = skippedTargets.length;
    if (!targets.length) {
      setStats({ added: 0, skipped, dupes: 0 });
      setProgress({ done: skipped, total: skipped });
      setError(null);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setRunningSafe(true);
    setError(null);
    setStats({ added: 0, skipped, dupes: 0 });
    setProgress({ done: skipped, total: skipped + targets.length });

    const seen = loadSeenEmails();
    let added = 0;
    let dupes = 0;

    const concurrency = 8;
    let cursor = 0;

    const worker = async () => {
      while (cursor < targets.length) {
        if (controller.signal.aborted) return;
        const index = cursor;
        cursor += 1;
        const domain = targets[index];
        const company = companyByDomain.get(domain) || {};

        try {
          const response = await axios.post(
            API_URL,
            { domain },
            { signal: controller.signal, timeout: 120000 }
          );
          const body = response.data || {};
          const found = Array.isArray(body.emails)
            ? body.emails
            : body.email
              ? [
                  {
                    email: body.email,
                    classification: body.classification,
                    verification: body.verification,
                    score: null,
                  },
                ]
              : [];

          const candidates = found
            .filter((e) => e && e.email)
            .map((e) => ({
              email: e.email,
              domain,
              classification: e.classification,
              verification: e.verification,
              score: e.score,
              product_name: company.product_name || '',
              tagline: company.tagline || '',
              website: company.website || '',
              launchedAt: company.launchedAt || '',
            }));

          const fresh = [];
          let localDupes = 0;
          for (const row of candidates) {
            const key = row.email.toLowerCase();
            if (seen.has(key)) {
              localDupes += 1;
            } else {
              fresh.push(row);
            }
          }

          if (fresh.length) {
            const result = mergeEmails(fresh);
            fresh.forEach((r) => seen.add(r.email.toLowerCase()));
            added += result.added.length;
            onEmailsChange(result.emails);
          }
          dupes += localDupes;
          markDomainPulled(domain);
          setPulledRevision((n) => n + 1);
          setStats({ added, skipped, dupes });
        } catch (err) {
          if (axios.isCancel(err) || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
            return;
          }
          setError((prev) => prev || err.message || 'Request failed');
        } finally {
          setProgress((p) => ({ ...p, done: Math.min(p.total, p.done + 1) }));
        }
      }
    };

    try {
      await Promise.all(
        Array.from({ length: Math.min(concurrency, targets.length) }, () => worker())
      );
    } finally {
      setRunningSafe(false);
      abortRef.current = null;
      onEmailsChange(loadEmails());
    }
  };

  const stop = () => {
    abortRef.current?.abort();
    setRunningSafe(false);
  };

  const startGrabRef = useRef(startGrab);
  startGrabRef.current = startGrab;
  const stopRef = useRef(stop);
  stopRef.current = stop;

  useEffect(() => {
    const offStart = onCommand('start_grab_emails', (payload) =>
      startGrabRef.current?.(payload || {})
    );
    const offStop = onCommand('stop_grab_emails', () => stopRef.current?.());
    const offAppend = onCommand('set_contact_append_mode', (payload) => {
      if (!payload || typeof payload.append !== 'boolean') return;
      setAppendMode(payload.append);
      saveContactAppendMode(payload.append);
    });
    return () => {
      offStart();
      offStop();
      offAppend();
    };
  }, []);

  const handleClear = () => {
    if (!window.confirm('Clear all contacts?')) return;
    clearEmails();
    clearSeenEmails();
    clearPulledDomains();
    onEmailsChange([]);
    setPulledRevision((n) => n + 1);
    setStats({ added: 0, skipped: 0, dupes: 0 });
  };

  const handleHideEmail = (email) => {
    onHiddenEmailsChange?.(hideEmail(email));
  };

  const handleUnhideEmail = (email) => {
    onHiddenEmailsChange?.(unhideEmail(email));
  };

  const addBlacklist = (e) => {
    e.preventDefault();
    const value = blacklistDraft.trim().toLowerCase();
    if (!value || !value.includes('@')) return;
    onHiddenEmailsChange?.(hideEmail(value));
    setBlacklistDraft('');
  };

  return (
    <div className="h-full flex flex-col min-w-0">
      <header className="px-8 pt-6 pb-4 border-b border-paper-line">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-[40px] font-bold tracking-tight text-ink leading-[1.15]">
              Contacts
            </h1>
            <p className="text-[14px] text-ink-muted mt-1.5 max-w-xl">
              One contact per address, nested under its domain. Pulled domains are saved in Redis — stop and resume anytime.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="relative" ref={blacklistRef}>
              <button
                type="button"
                onClick={() => setBlacklistOpen((v) => !v)}
                className="text-[13px] text-ink-muted hover:text-ink hover:bg-wash rounded-[4px] px-1.5 py-1"
              >
                Blacklist
                <span className="ml-1.5 text-[12px] text-ink-faint tabular-nums">
                  {hiddenEmails.length}
                </span>
              </button>
              {blacklistOpen && (
                <div className="absolute right-0 top-full mt-1.5 w-72 bg-white border border-paper-line rounded-md shadow-popover z-50 p-3">
                  <div className="text-[12px] font-medium text-ink mb-2">Hidden contacts</div>
                  <p className="text-[12px] text-ink-faint mb-2">
                    Still on the domain. Hidden from this list, Emails, and CSV.
                  </p>
                  <form onSubmit={addBlacklist} className="flex gap-1.5 mb-2">
                    <input
                      value={blacklistDraft}
                      onChange={(e) => setBlacklistDraft(e.target.value)}
                      placeholder="name@company.com"
                      className="flex-1 px-2 py-1 text-[12px] border border-paper-line rounded-md"
                    />
                    <button
                      type="submit"
                      className="px-2 py-1 text-[12px] rounded-md border border-paper-strong bg-white hover:bg-wash text-ink"
                    >
                      Add
                    </button>
                  </form>
                  <ul className="max-h-48 overflow-y-auto space-y-1">
                    {hiddenEmails.length === 0 ? (
                      <li className="text-[11px] text-ink-faint py-2">Empty</li>
                    ) : (
                      hiddenEmails.map((email) => (
                        <li
                          key={email}
                          className="flex items-center gap-2 text-[11px] py-1 border-b border-paper-line/60"
                        >
                          <span className="flex-1 truncate text-ink">{email}</span>
                          <button
                            type="button"
                            onClick={() => handleUnhideEmail(email)}
                            className="text-ink-faint hover:text-ink"
                          >
                            Unhide
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                  {hiddenEmails.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm('Clear entire contact blacklist?')) {
                          onHiddenEmailsChange?.(saveHiddenEmails([]));
                        }
                      }}
                      className="mt-2 text-[11px] text-ink-faint hover:text-ink"
                    >
                      Clear blacklist
                    </button>
                  )}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setExportOpen(true)}
              className="text-[13px] text-ink-muted hover:text-ink hover:bg-wash rounded-[4px] px-1.5 py-1"
            >
              Export
              <span className="ml-1.5 text-[12px] text-ink-faint tabular-nums">
                {filteredEmails.length}
              </span>
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={startGrab}
            disabled={running || domains.length === 0}
            className="text-[13px] px-3 py-1.5 rounded-[4px] bg-cta text-white hover:bg-cta-hover disabled:opacity-50"
          >
            {running
              ? 'Pulling…'
              : appendMode
                ? `Pull new (${remainingDomains.length} remaining)`
                : `Pull contacts (${domains.length} domains)`}
          </button>
          {running && (
            <button
              type="button"
              onClick={stop}
              className="text-[13px] px-3 py-1.5 rounded-[4px] border border-paper-strong bg-white hover:bg-wash"
            >
              Stop
            </button>
          )}
          <label
            className={`inline-flex items-center gap-1.5 text-[13px] select-none ${
              running ? 'opacity-40' : 'text-ink-muted hover:text-ink cursor-pointer'
            }`}
            title={
              appendMode
                ? 'Keep existing contacts. Skip domains already saved in Redis — no lookup.'
                : 'Replace the list, then pull every domain.'
            }
          >
            <input
              type="checkbox"
              checked={appendMode}
              disabled={running}
              onChange={(e) => {
                const next = e.target.checked;
                setAppendMode(next);
                saveContactAppendMode(next);
              }}
              className="rounded border-paper-strong"
            />
            Append
          </label>
          <span className="text-[13px] text-ink tabular-nums">
            {savedOnList} of {domains.length} in Redis
            {remainingDomains.length > 0 ? ` · ${remainingDomains.length} left` : ''}
          </span>
          <button
            type="button"
            onClick={handleClear}
            disabled={running || emails.length === 0}
            className="text-[13px] text-ink-muted hover:text-ink disabled:opacity-40"
          >
            Clear contacts
          </button>
          {filteredEmails.length > 0 && (
            <button
              type="button"
              onClick={() => onGoEmails?.()}
              className="ml-auto text-[13px] text-ink-muted hover:text-ink hover:bg-wash rounded-[4px] px-1.5 py-1"
            >
              Write emails →
            </button>
          )}
        </div>

        {domains.length > 0 && (
          <div className="mt-3 h-1 bg-paper-line rounded-full overflow-hidden">
            <div
              className="h-full bg-ink transition-all duration-300"
              style={{
                width: `${Math.round((savedOnList / domains.length) * 100)}%`,
              }}
            />
          </div>
        )}

        <div className="mt-3 flex flex-wrap gap-4 text-[12px] text-ink-faint tabular-nums">
          <span>
            {savedOnList}/{domains.length} saved
          </span>
          <span>{remainingDomains.length} remaining</span>
          <span>{filteredEmails.length} contacts</span>
          {savedCount > savedOnList && (
            <span>{savedCount} in Redis</span>
          )}
          {running && stats.added > 0 && <span>{stats.added} new this run</span>}
          {running && stats.skipped > 0 && <span>{stats.skipped} skipped</span>}
          {running && stats.dupes > 0 && <span>{stats.dupes} duplicate emails</span>}
        </div>
      </header>

      {error && (
        <div className="mx-8 mt-4 px-3 py-2 text-[12px] border border-paper-line bg-callout text-ink">
          {error}
        </div>
      )}

      <DataTable
        bodyRef={listRef}
        trailing={
          emails.length > 0 ? (
            <ContactFilters
              filters={emailFilters}
              onChange={onEmailFiltersChange}
            />
          ) : null
        }
          columns={[
          { key: 'contact', label: 'Contact', first: true },
          { key: 'type', label: 'Type' },
          { key: 'status', label: 'Status' },
          { key: 'scraped', label: 'Date scraped' },
          { key: 'launched', label: 'Date launched' },
          { key: 'actions', label: '', last: true, align: 'right', width: '4rem' },
        ]}
        empty={
          emails.length === 0
            ? domains.length === 0
              ? 'Scrape domains first, then pull contacts.'
              : 'Pull contacts to attach people to each domain.'
            : filteredEmails.length === 0
              ? 'No contacts match the filters. Open Filter to widen them.'
              : null
        }
      >
        {grouped.map((group) => {
          const productName = group.company?.product_name || '';
          const groupLabel = productName || group.domain;
          const groupMeta =
            productName && group.domain && group.domain !== productName
              ? group.domain
              : null;
          return (
            <React.Fragment key={group.domain}>
              <GroupTr
                colSpan={7}
                label={groupLabel}
                meta={groupMeta}
                count={group.contacts.length}
              />
              {group.contacts.map((row) => {
                const scraped = formatShortDate(row.savedAt);
                const launched = formatShortDate(
                  row.launchedAt || group.company?.launchedAt
                );
                return (
                  <Tr key={`${row.email}-${row.domain}`}>
                    <Td first className="text-ink font-medium">
                      <span className="block truncate max-w-[22rem]">{row.email}</span>
                    </Td>
                    <Td className="whitespace-nowrap">
                      {row.classification ? (
                        <Chip tone={classificationTone(row.classification)}>
                          {row.classification}
                        </Chip>
                      ) : (
                        <span className="text-ink-faint">—</span>
                      )}
                    </Td>
                    <Td className="whitespace-nowrap">
                      {row.verification ? (
                        <Chip tone={verificationTone(row.verification)}>
                          {row.verification}
                        </Chip>
                      ) : (
                        <span className="text-ink-faint">—</span>
                      )}
                    </Td>
                    <Td className="whitespace-nowrap tabular-nums text-ink">
                      {scraped || <span className="text-ink-faint">—</span>}
                    </Td>
                    <Td className="whitespace-nowrap tabular-nums text-ink">
                      {launched || <span className="text-ink-faint">—</span>}
                    </Td>
                    <TdAction>
                      <button
                        type="button"
                        onClick={() => handleHideEmail(row.email)}
                        className="text-[12px] text-ink-muted hover:text-ink"
                      >
                        Hide
                      </button>
                    </TdAction>
                  </Tr>
                );
              })}
            </React.Fragment>
          );
        })}
      </DataTable>

      <ExportEmailsModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        emailFilters={emailFilters}
        onEmailFiltersChange={onEmailFiltersChange}
        filteredEmails={filteredEmails}
      />
    </div>
  );
}

export default ContactsView;
